import { errorText } from '@/api/client';
import { useShifts } from '@/api/data';
import { useLabour, useSales, useSaveLabour, useSaveSales } from '@/api/labour';
/**
 * Lønnsprosent, looking back (0017 P0.5; renamed 2026-09-27, planning moved into the Vaktplan's
 * Bemanning mot behov row): wage cost per day against the sales the
 * manager types, against a target. Sales, target and rates are the company's, on the server since
 * 2026-09-25 (api/labour.ts); they were in each browser before.
 *
 * Since the manager redesign (2026-10-09) it is the Lønnsprosent tab of Timer til lønn: the same
 * header, its own week or month and its own company (the active one), and its own export. A day with
 * no sales is missing, not zero; 0 is a day that took nothing; a figure under 1 000 is a unit slip
 * to check. It is the plan's cost, not the pay run's.
 */
import { T } from '@/i18n';
import { breakPolicy, oslo, plannedHours, plannedPaidHours, today } from '@/rules';
import { type Paid, costOf, hourlyRateOf, ratesOf } from '@/rules/labour';
import { LocalFiguresOffer } from '@/shell/LocalFiguresOffer';
import { useTenant } from '@/shell/tenant';
import { Button, LoadError, Skeleton, toast } from '@/ui';
import { PeriodControl } from '@/ui/PeriodControl';
import { monthLabel } from '@/ui/format';
import { DN, DNL, fmtDs, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Notice, Status, TabPanel } from '@/ui/page';
import { addDays, dow, isoWeek, mondayOf, weekDays } from '@timefly/core';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LonnHead } from './lonn/Head';

const kr = (n: number) => Math.round(n).toLocaleString('nb-NO');

/** Every day of a YYYY-MM, as YYYY-MM-DD. */
export function monthDays(ym: string): string[] {
  const [y, m] = ym.split('-').map(Number);
  const n = new Date(Date.UTC(y ?? 2026, m ?? 1, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${ym}-${String(i + 1).padStart(2, '0')}`);
}

/** Steps months without ever landing on the 31st of a short one. */
export function shiftMonth(ym: string, by: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// the shared month label, re-exported for the pages that import it from here
export { monthLabel };

/** What a day's sales say: none typed, a typed 0, a figure under 1 000 to check, or one to count. */
export type SalesState = 'missing' | 'zero' | 'odd' | 'ok';
export const salesState = (v: number | undefined): SalesState =>
  v == null ? 'missing' : v === 0 ? 'zero' : v < 1000 ? 'odd' : 'ok';

/** The figure typed in a cell: empty clears the day (null), anything else is the kroner typed. */
export const typedSales = (raw: string): number | null => {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const v = Number(t);
  return Number.isFinite(v) && v >= 0 ? v : null;
};

import { TRIPLETEX } from './lonn/payrollSystem';
import { PowerOfficeRevenue } from './omsetning/PowerOfficeRevenue';
import { isUnplanned } from './oversikt/day';

export default function Omsetning() {
  const { tenant, mode } = useTenant();
  const [monday, setMonday] = useState(() => mondayOf(today()));
  // "Labor dabar galima pasitikrinti tik weekly, bet reikėtų ir monthly varianto" (designer's
  // report, 2026-09-21). A month shows every day rather than a weekly summary, because sales
  // are typed in per day and a summary would take the entry away.
  const [span, setSpan] = useState<'week' | 'month'>('week');
  const [ym, setYm] = useState(() => today().slice(0, 7));
  const days = span === 'week' ? weekDays(monday) : monthDays(ym);
  const from = days[0] ?? monday;
  const to = days[days.length - 1] ?? monday;
  const labour = useLabour();
  const rates = ratesOf(labour.rates);
  const shiftsQ = useShifts(from, to);
  const salesQ = useSales(from, to);
  const { sales } = salesQ;
  const target = labour.target;
  const saveSales = useSaveSales();
  const saveLabour = useSaveLabour();
  // the plan only (#263): a clock-in with no planned shift has no planned hours, as on Dagen
  const shifts = (shiftsQ.data?.shifts ?? []).filter((s) => s.user_id != null && !isUnplanned(s));
  // every figure waits for the plan, the rates and the sales; until then it is unknown, not 0 (#262, #263)
  const known = shiftsQ.isSuccess && !labour.pending && !labour.failed && salesQ.ready;
  const failed: { what: string; retry: () => unknown }[] = [];
  if (shiftsQ.isError) failed.push({ what: T('vaktplanen'), retry: () => shiftsQ.refetch() });
  if (labour.failed) failed.push({ what: T('lønnsinnstillingene'), retry: labour.retry });
  if (salesQ.failed) failed.push({ what: T('omsetningen'), retry: salesQ.retry });
  const dash = (v: string) => (known ? v : '–');
  const rota = mode === 'rota';
  const policy = breakPolicy(tenant ?? undefined);

  const rows = days.map((d) => {
    // a shift counts on the day it starts, a night into the next day included, cost and hours alike
    const ss = shifts.filter((s) => (oslo(s.start_time)?.date ?? '') === d);
    // Gross pay first, then pension and the employer's contribution on top (rules/labour.ts).
    // This used to be `hourly_rate ?? 0` times a hardcoded 1.141: no pension at all, and a
    // monthly-salaried person counted as free (designer's report, 2026-09-21).
    const wage = ss.reduce(
      (a, s) => a + plannedPaidHours(s, policy) * hourlyRateOf(s.user as Paid | null),
      0,
    );
    const cost = costOf(wage, rates).total;
    const sl = sales[d];
    const state = salesState(sl);
    return {
      d,
      // planned start to planned end, before the break; a punch never changes it (#263)
      h: ss.reduce((a, s) => a + plannedHours(s), 0),
      cost,
      sales: sl,
      state,
      // a figure under 1 000 kr is a unit slip ("1" meant 1 kr and gave 876 298 %, owner 2026-09-27):
      // no percentage, and it says to check the amount
      pct: known && state === 'ok' && sl != null ? (cost / sl) * 100 : null,
    };
  });
  const tc = rows.reduce((a, r) => a + r.cost, 0);
  const ts = rows.reduce((a, r) => a + (r.sales ?? 0), 0);
  // the period's share over the days that have real sales only: a week's cost against one day's
  // sales read 5 738 687 % (owner, 2026-09-27)
  const counted = rows.filter((r) => r.pct != null);
  const cc = counted.reduce((a, r) => a + r.cost, 0);
  const cs = counted.reduce((a, r) => a + (r.sales ?? 0), 0);
  const wk = cs ? (cc / cs) * 100 : null;
  const missing = rows.filter((r) => r.state === 'missing').length;
  const odd = rows.filter((r) => r.state === 'odd').length;
  const setSales = (d: string, v: number | null) =>
    saveSales.mutate(
      { [d]: v },
      {
        onSuccess: () =>
          toast(
            v == null
              ? T('Omsetning fjernet for {day}', { day: `${DNL[dow(d)]} ${fmtDs(d)}` })
              : T('Omsetning lagret for {day}', { day: `${DNL[dow(d)]} ${fmtDs(d)}` }),
          ),
        onError: (e) => toast(errorText(e)),
      },
    );
  const setTarget = (v: number) =>
    saveLabour.mutate(
      { revision: labour.revision, wage_target_pct: v },
      { onError: (e) => toast(errorText(e)) },
    );
  const period = span === 'week' ? `${T('Uke')} ${isoWeek(monday)}` : monthLabel(ym);

  /** The table as it stands, as a file: the days, the plan's cost, the sales and their state. */
  const exportCsv = () => {
    // the file says what the table says: nothing while a figure is still unknown (#262)
    if (!known) return toast(T('Vent til tallene er hentet.'));
    const state = {
      missing: 'Mangler',
      zero: 'Registrert 0',
      odd: 'Sjekk beløpet',
      ok: 'Registrert',
    };
    const lines = [
      ['Dato', 'Planlagte timer', 'Lønnskostnad (kr)', 'Omsetning ekskl. mva (kr)', 'Omsetning', 'Lønn %'],
      ...rows.map((r) => [
        r.d,
        r.h.toFixed(2).replace('.', ','),
        String(Math.round(r.cost)),
        r.sales == null ? '' : String(r.sales),
        state[r.state],
        r.pct == null ? '' : r.pct.toFixed(1).replace('.', ','),
      ]),
    ];
    const csv = `\ufeff${lines.map((l) => l.join(';')).join('\n')}\n`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `lonnsprosent-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast(T('Lønnsprosent lastet ned'));
  };

  const head = (
    <LonnHead
      tab="kost"
      action={
        <Button tone="pri" onClick={exportCsv} disabled={!tenant || !rota || !known}>
          <Icon name="download" />
          {T('Eksporter lønnsprosent')}
        </Button>
      }
    />
  );
  if (!tenant)
    return (
      <div className="lonn oms">
        {head}
        <TabPanel tab="kost">
          <Skeleton />
        </TabPanel>
      </div>
    );
  if (!rota)
    return (
      <div className="lonn oms">
        {head}
        <TabPanel tab="kost">
          <Notice tone="info" title={T('Lønnsprosent krever en vaktplan')}>
            {T(
              'Lønnsprosent per dag krever omsetningstall og en plan. Denne bedriften har ingen av delene her.',
            )}{' '}
            <Link to="/timeliste">
              <Icon name="clock" />
              {T('Til timelisten')}
            </Link>
            .
          </Notice>
        </TabPanel>
      </div>
    );

  const W = 700;
  const base = 110;
  const sc = 1.8;

  return (
    <div className="lonn oms">
      {head}
      <TabPanel tab="kost">
        <LocalFiguresOffer />
        <div className="mbar">
          <fieldset className="mchips" aria-label={T('Periode')}>
            {(['week', 'month'] as const).map((v) => (
              <button
                type="button"
                key={v}
                aria-pressed={span === v}
                onClick={() => setSpan(v)}
                data-testid={`span-${v}`}
              >
                {v === 'week' ? T('Uke') : T('Måned')}
              </button>
            ))}
          </fieldset>
          <PeriodControl
            period={span === 'week' ? 'week' : 'month'}
            month={span !== 'week'}
            value={span === 'week' ? monday : ym}
            onChange={(v) => (span === 'week' ? setMonday(mondayOf(v)) : setYm(v))}
            prevLabel={span === 'week' ? T('Forrige uke') : T('Forrige måned')}
            nextLabel={span === 'week' ? T('Neste uke') : T('Neste måned')}
            onPrev={() => (span === 'week' ? setMonday(addDays(monday, -7)) : setYm(shiftMonth(ym, -1)))}
            onNext={() => (span === 'week' ? setMonday(addDays(monday, 7)) : setYm(shiftMonth(ym, 1)))}
            extra={
              (span === 'week' ? monday !== mondayOf(today()) : ym !== today().slice(0, 7)) && (
                <Button
                  ghost
                  onClick={() => {
                    setMonday(mondayOf(today()));
                    setYm(today().slice(0, 7));
                  }}
                >
                  {T('I dag')}
                </Button>
              )
            }
          >
            <span data-testid="period">
              {span === 'week' ? `${period} · ${fmtDs(monday)}–${fmtDs(addDays(monday, 6))}` : period}
            </span>
          </PeriodControl>
          <label className="mf">
            {T('Mål lønnsprosent')}
            <input
              className="inp ltarget"
              aria-label={T('Mål lønnsprosent')}
              type="number"
              min={1}
              max={100}
              key={target}
              defaultValue={target}
              onBlur={(e) =>
                Number(e.target.value) > 0 &&
                Number(e.target.value) !== target &&
                setTarget(Number(e.target.value))
              }
            />
          </label>
          <span className="sp" />
          <div className="lsum">
            {!known ? (
              <output aria-busy={failed.length === 0}>
                <b>{failed.length ? T('Tallene mangler') : T('Henter tallene for {period}', { period })}</b>
              </output>
            ) : wk == null ? (
              <b>{T('Ingen omsetning lagt inn')}</b>
            ) : (
              <b data-testid="week-pct">
                {Math.round(wk)} % {span === 'week' ? T('denne uken') : T('denne måneden')}
              </b>
            )}
            <span>
              {T('Mål {n} %', { n: target })} ·{' '}
              {T('{a} av {b} dager med omsetning', {
                a: counted.length,
                b: rows.length,
              })}
              {odd ? ` · ${T('{n} må kontrolleres', { n: odd })}` : ''}
            </span>
          </div>
        </div>
        {failed.length > 0 && (
          <LoadError
            what={failed.map((f) => f.what).join(', ')}
            onRetry={() => {
              for (const f of failed) f.retry();
            }}
          />
        )}
        <section className="mcard">
          <div className="mch">
            <div>
              <h2>{T('Lønnskostnad mot omsetning')}</h2>
              <div className="sub">
                {T('Planlagte vakter')},{' '}
                {T('inkl. {holiday} % feriepenger, {aga} % arbeidsgiveravgift og {pension} % pensjon', {
                  holiday: num(rates.holidayPct),
                  aga: num(rates.agaPct),
                  pension: num(rates.pensionPct),
                })}{' '}
                · {tenant.name} · {T('mot mål {target} %', { target })}
              </div>
            </div>
          </div>
          <div className="lchart">
            <svg className="svg" viewBox={`0 0 ${W} 140`} role="img" aria-label={T('Lønnsprosent per dag')}>
              <line x1={40} y1={base} x2={W - 10} y2={base} style={{ stroke: 'var(--base)' }} />
              {rows.map((r, i) => {
                // the step follows the number of days: a fixed 82 fitted a week and ran a month's
                // bars far past the chart
                const step = (W - 60) / Math.max(rows.length, 1);
                const bw = Math.min(50, step * 0.7);
                const x = 50 + i * step;
                const dense = rows.length > 10;
                const h = r.pct == null ? 0 : Math.min(r.pct, 50) * sc;
                return (
                  <g key={r.d}>
                    {r.pct == null ? (
                      <text
                        x={x + bw / 2}
                        y={100}
                        textAnchor="middle"
                        fontSize={10}
                        style={{ fill: 'var(--ink3)' }}
                      >
                        {dense || !known
                          ? '–'
                          : r.state === 'odd'
                            ? T('sjekk beløpet')
                            : r.state === 'zero'
                              ? T('0 kr')
                              : T('ingen omsetning')}
                      </text>
                    ) : (
                      <>
                        <rect
                          x={x}
                          y={base - h}
                          width={bw}
                          height={h}
                          style={{
                            fill:
                              r.pct > target + 5
                                ? 'var(--dgr-l)'
                                : r.pct > target
                                  ? 'var(--warn-l)'
                                  : 'var(--slate-l)',
                          }}
                        />
                        <text
                          x={x + bw / 2}
                          y={base - h - 6}
                          textAnchor="middle"
                          fontSize={dense ? 8 : 11}
                          fontWeight={700}
                          style={{
                            fill: r.pct > target + 5 ? 'var(--dgr-i)' : 'var(--ink1)',
                          }}
                        >
                          {Math.round(r.pct)}
                        </text>
                      </>
                    )}
                    <text
                      x={x + bw / 2}
                      y={126}
                      textAnchor="middle"
                      fontSize={dense ? 8 : 10}
                      fontWeight={600}
                      style={{ fill: 'var(--ink3)' }}
                    >
                      {dense ? Number(r.d.slice(8, 10)) : `${DN[dow(r.d)]} ${fmtDs(r.d)}`}
                    </text>
                  </g>
                );
              })}
              <line
                x1={40}
                y1={base - target * sc}
                x2={600}
                y2={base - target * sc}
                style={{ stroke: 'var(--ok-l)' }}
                strokeWidth={2}
                strokeDasharray="6 4"
              />
              <text
                x={606}
                y={base - target * sc + 3}
                fontSize={10}
                fontWeight={700}
                style={{ fill: 'var(--ok-i)' }}
              >
                {T('Mål {target} %', { target })}
              </text>
            </svg>
          </div>
          {/* the accounts' revenue for a company on PowerOffice Go or Tripletex, a month at a time (#183, #187) */}
          {span === 'month' && tenant?.payroll_system === 'poweroffice' && (
            <PowerOfficeRevenue ym={ym} target={target} />
          )}
          {span === 'month' && tenant?.payroll_system === 'tripletex' && (
            <PowerOfficeRevenue ym={ym} target={target} sys={TRIPLETEX} />
          )}
          <table className="mt cards" data-testid="omsetning">
            <thead>
              <tr>
                <th>{T('Dag')}</th>
                <th className="num">{T('Planlagte timer')}</th>
                <th className="num">{T('Lønnskostnad')}</th>
                <th className="num">{T('Omsetning ekskl. mva (kr)')}</th>
                <th className="num">{T('Lønn %')}</th>
                <th>
                  <span className="sr">{T('Dagen')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.d}>
                  <td className="lday">
                    {DNL[dow(r.d)]} {fmtDs(r.d)}
                  </td>
                  <td className="num" data-k={T('Planlagt')}>
                    {dash(`${num(r.h)} t`)}
                  </td>
                  <td className="num" data-k={T('Kostnad')}>
                    {dash(`${kr(r.cost)} kr`)}
                  </td>
                  <td className="num">
                    <input
                      className="inp"
                      type="number"
                      min={0}
                      step="100"
                      aria-label={`Omsetning ${fmtDs(r.d)}`}
                      aria-describedby={r.state === 'odd' ? `odd-${r.d}` : undefined}
                      placeholder={salesQ.ready ? T('Mangler') : ''}
                      // a day typed before the saved figures arrive would overwrite one it never saw
                      disabled={!salesQ.ready}
                      key={`${r.d}-${r.sales ?? 'none'}`}
                      defaultValue={r.sales ?? ''}
                      onBlur={(e) => {
                        const v = typedSales(e.target.value);
                        if (v === (r.sales ?? null)) return;
                        // whole kroner: "1" read as 1 kr and gave 876 298 % (owner, 2026-09-27)
                        if (v != null && v > 0 && v < 1000) {
                          toast(T('Skriv hele kronebeløpet, f.eks. 32 000.'));
                          return;
                        }
                        setSales(r.d, v);
                      }}
                    />
                  </td>
                  <td
                    className="num"
                    data-testid="day-pct"
                    aria-label={r.state === 'missing' ? T('Omsetning mangler') : undefined}
                    id={r.state === 'odd' ? `odd-${r.d}` : undefined}
                  >
                    {r.state === 'odd' ? (
                      <span className="lodd">{T('Sjekk beløpet')}</span>
                    ) : r.state === 'zero' ? (
                      <span className="lmiss">{T('0 kr omsetning')}</span>
                    ) : r.pct == null ? (
                      '–'
                    ) : (
                      <Status tone={r.pct > target + 5 ? 'dgr' : r.pct > target ? 'warn' : 'ok'}>
                        {Math.round(r.pct)} %
                      </Status>
                    )}
                  </td>
                  <td>
                    <Link className="mlink" to={`/dagen?d=${r.d}`}>
                      <Icon name="calendar" />
                      {T('Dagen')}
                    </Link>
                  </td>
                </tr>
              ))}
              <tr className="tot">
                <td>{period}</td>
                <td className="num" data-k={T('Planlagt')}>
                  {dash(`${num(rows.reduce((a, r) => a + r.h, 0))} t`)}
                </td>
                <td className="num" data-k={T('Kostnad')}>
                  {dash(`${kr(tc)} kr`)}
                </td>
                <td className="num" data-k={T('Omsetning')}>
                  {missing === rows.length ? '–' : `${kr(ts)} kr`}
                </td>
                <td className="num" data-k={T('Lønn %')}>
                  {wk == null ? '–' : `${Math.round(wk)} %`}
                  {wk != null && counted.length < rows.length && (
                    <small>{T('{a} av {b} dager', { a: counted.length, b: rows.length })}</small>
                  )}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </section>
        <p className="mfoot" style={{ maxWidth: '72ch' }}>
          {T(
            'Lønnskostnaden er planlagte timer ganger timesats, med feriepenger, arbeidsgiveravgift og pensjon fra Innstillinger → Lønn; for fastlønn regnes timesatsen fra månedslønnen og avtalt uke. Det er planen, ikke lønnskjøringen. Omsetningen legges inn per dag i hele kroner og lagres for hele bedriften. Tomt felt betyr at omsetningen mangler; 0 er registrert null. Et beløp under 1 000 kr må kontrolleres før dagen telles.',
          )}{' '}
          {T(
            'Planlagte timer er hele vakten fra planlagt start til planlagt slutt, før pause. Lønnskostnaden regnes på de samme vaktene etter ubetalt pause etter bedriftens pauseregel. Stemplinger endrer ikke tallene. En vakt over midnatt telles på dagen den begynner, og stempling uten planlagt vakt er ikke med. Lønnsprosenten for perioden regnes bare på dager med omsetning: både kostnaden og omsetningen for en dag uten omsetning holdes utenfor.',
          )}
        </p>
      </TabPanel>
    </div>
  );
}
